import { Observable, ObservableCollection } from '@acies/observables';
import type { IBom } from '../../domain/calculations';
import type { IPresentedParameter, StockConfiguration } from '../../domain/configurations';
import { type DrawingSide, drawingStyleSupplement } from '../../domain/drawings';
import type { IPositions } from '../../domain/positions';
import { positions as seedPositions } from '../../domain/positions.seed';

/**
 * Orders two positions by the place they sit in, which is what their place is for.
 *
 * Written out rather than subtracted, since an ObservableCollection asks its sort function for one of three answers rather than for any number.
 */
function byPlace(first: IPositions, second: IPositions): -1 | 0 | 1 {
	if (first.position === second.position) {
		return 0;
	}
	return first.position < second.position ? -1 : 1;
}

/**
 * Every position the Configurator knows about, in the order they sit in.
 *
 * Sorted here rather than by whoever renders them: the order is a property of the positions themselves, so a list, an export and a calculation all read them the same way. Sorting it where the collection is built also means no component has to have run for the order to hold.
 *
 * Seeded from `positions.seed`, so the Configurator has something to show until it asks the API for the real ones. Copied rather than handed over: the collection keeps the array it is given and pushes into it, so the seed would grow every time a position was added.
 */
const positions = new ObservableCollection<IPositions>([...seedPositions]);
positions.sortFunction = byPlace;

/**
 * The position the user is working on, or null when they are working on none.
 *
 * Selecting a position in the positions list puts it here, and de-selecting puts null back. It is one position rather than a collection: the sections that read it — the drawing, the answers — show the one position that is being worked on.
 *
 * An Observable rather than a plain value, so those sections re-render from a subscription when the selection changes, without the list having to know they exist.
 */
const currentPosition = new Observable<IPositions | null>(null);

/**
 * The parameters of the position being worked on, which the answers list shows.
 *
 * A model's parameters or a stock item's, whichever the selected position is configured as — see `IPresentedParameter`.
 *
 * Empty when nothing is selected, when the selection is a text position, and when a model or stock position has not been configured. All three are an empty answers list, which is what an empty collection already says — so unlike {@link currentDrawing} there is no null to tell apart.
 *
 * An ObservableCollection rather than an Observable of an array, and for one reason: changing a parameter's value is a `replace()`, which tells the list exactly which row changed so it redraws that row alone. An array would be a new array every keystroke, and the input the user is typing in would be torn down under
 * the cursor.
 *
 * Filled with `setItems`, which is why that method exists: clearing and then adding tells the list twice, and a list that rebuilds on being told would build every row twice for one selection.
 */
const currentParameters = new ObservableCollection<IPresentedParameter>();

/**
 * The stock configuration of the position being worked on, or null when it is not a configured stock position.
 *
 * What the row above the answers list reads: the configurations the item comes in are picked there, and it is shown only for a configuration that links the list they are picked from — see `StockConfiguration.itemConfigurations`. Null covers nothing selected, a model or text position, and a stock position not
 * configured yet or whose configuring failed — all of them a row that is not shown.
 *
 * Written beside {@link currentParameters}, by whoever writes those, so the row and the answers below it are always of the same configuration.
 */
const currentStockConfiguration = new Observable<StockConfiguration | null>(null);

/**
 * What the engine worked out about the position being worked on, or null when there is nothing worked out to show.
 *
 * Null covers nothing selected, a text or stock position the engine never prices, and a model position nobody has priced yet. All three are a properties panel with nothing in it, which is why they are one value here rather than three — the panel hides its properties on null and shows them on anything else.
 *
 * Read off the selected position rather than fetched, the way {@link currentParameters} is: a priced position carries its own BOM, so selecting one is a read and not a round trip.
 *
 * An Observable of the whole BOM rather than one per measurement, because they arrive together and are shown together: a calculation lands as one answer, and the panel is written from it in one go.
 */
const currentProperties = new Observable<IBom | null>(null);

/**
 * What went wrong the last time the position being worked on was sent to the engine, or null when nothing did.
 *
 * Null covers nothing selected, a text or stock position the engine is never sent, and a model position whose last answer went through. All three are the same thing to the two places that show it — the geometry dialog, where the drawing would be, and the properties panel, under the properties — so they are one value
 * here rather than three: a message is shown on a text and hidden on null.
 *
 * Read off the selected position rather than kept apart from it, the way {@link currentProperties} is: a position carries its own error — see `ModelPosition.error` — so selecting one is a read, and a refusal that lands for the selected position is written here by whoever writes it to the position. Not read off
 * {@link currentPosition} by the two places themselves, because that holds the object the user selected, and a position that has just been refused is a new object.
 */
const currentError = new Observable<string | null>(null);

/**
 * The drawing of the position being worked on, or null when there is none to show.
 *
 * Null covers more than one thing, and deliberately for now: nothing is selected, the selection is a text or stock position the engine never draws, the position has not been priced yet, the engine could not price it, or the drawing itself could not be fetched. All six show the same empty section. The day the section
 * has to tell them apart, this grows from an element into something that carries the reason — which is a change to make when the UI asks for it rather than a shape to guess at now.
 *
 * The element rather than the markup, so whoever shows it has nothing left to do that could fail: parsing is done, and what is here is either a drawing or nothing.
 */
const currentDrawing = new Observable<SVGSVGElement | null>(null);

/**
 * The stylesheets a drawing is styled by, in the order they are adopted.
 *
 * Starts as the supplement alone, which is a constant in this module and needs no CDN, so there is never a moment where a drawing is on screen with no styling at all. The machine replaces this with the full set — the engine's stylesheet, the supplement, and the customer's own colours when they have any — the first
 * time a drawing is shown.
 *
 * Not part of {@link currentDrawing}, because these are not per position: every drawing on the page is styled by the same sheets, and re-reading them for each selection would say otherwise.
 */
const currentDrawingStyles = new Observable<ReadonlyArray<CSSStyleSheet>>([drawingStyleSupplement]);

/**
 * Which side of the window the drawings show.
 *
 * The user's standing preference rather than a property of one drawing: someone comparing the inside of one position with the inside of the next keeps looking at insides until they say otherwise, so selecting another position does not send them back to the outside.
 *
 * Outside to begin with, which is what a drawing shows when nothing has turned it around.
 */
const currentDrawingSide = new Observable<DrawingSide>('outside');

/**
 * Whether the geometry dialog is on screen.
 *
 * Hidden to begin with: the dialog is only shown when the user asks for it.
 */
const geometryDialogShown = new Observable<boolean>(false);

/**
 * Whether the properties panel is on screen.
 *
 * Shown to begin with, and flipped by the show/hide button in the top bar — through the machine, so the panel and the button need not know of each other. The panel reads it and sets its own visibility.
 */
const propertiesPanelShown = new Observable<boolean>(true);

/**
 * Whether a configuration is being asked for, for the position being worked on: true from the moment one is asked for until it answers, fails or is overtaken.
 *
 * The selected position rather than any, because what reads it is a spinner standing where that position's drawing and properties will be: another position being worked up brings those no nearer, and a spinner over a stock position, which nothing is ever sent for, would turn for good. A position's own row says the
 * same about that position — see its `status`.
 *
 * Written by whoever counts the calls, which is `ConfiguratorMachine`: it counts them per position, and writes this again whenever the selection moves or a call for the selected position goes out or comes back.
 */
const configurationInProgress = new Observable<boolean>(false);

/**
 * Whether a calculation is being worked out, for the position being worked on: true from the moment one is asked for until it is priced, fails or is overtaken.
 *
 * The whole wait and not the one request: the engine takes a calculation on and is asked after it until it is done, which can take up to a minute. Counted the way {@link configurationInProgress} is, and for the selected position for the same reason.
 */
const calculationInProgress = new Observable<boolean>(false);

/**
 * The state the Configurator's components share.
 *
 * A module constant rather than something passed down, because the components that read it are web components: they are constructed by the browser from a tag name, so there is no constructor to hand anything to. Importing the model is how a component gets at it.
 *
 * An ObservableCollection rather than an array, so a list can be handed the collection itself and re-render from a subscription when a position is added or removed, without anyone telling it to.
 */
export const ConfiguratorModel = {
	calculationInProgress,
	configurationInProgress,
	currentDrawing,
	currentDrawingSide,
	currentDrawingStyles,
	currentError,
	currentParameters,
	currentPosition,
	currentProperties,
	currentStockConfiguration,
	geometryDialogShown,
	positions,
	propertiesPanelShown,
};
